import React, { useEffect, useState } from "react";
import Footer from "../components/Shared/Footer";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchCartAndItems, fetchAddonProducts, updateCart, addToCart, deleteCartItems, fetchCart } from "../api/ShopHelper";
import { ClipLoader } from 'react-spinners';
import { getUserId, getAccesstoken } from "../utils/auth";

const Cart = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [addonProducts, setAddonProducts] = useState([]);
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));
  const navigate = useNavigate();

  const user_id = getUserId();
  const access_token = getAccesstoken();

  useEffect(() => {
    if (access_token && user_id) {
      fetchCartItems();
    } else {
      setLoading(false);
      setCart([]);
    }
  }, [access_token, user_id, baseUrl]);

  const fetchCartItems = async () => {
    setLoading(true);
    const { cartItems, loading: newLoading } = await fetchCartAndItems(user_id, access_token);
    setCart(cartItems);
    setLoading(newLoading);
  };

  useEffect(() => {
    const fetchAddonProductsData = async () => {
      let cart_id = null;
      if (access_token && user_id) {
        cart_id = await fetchCart(user_id)
      }
      const productsData = await fetchAddonProducts();
      // Store only the relevant fields for each addon product
      const updatedAddonProducts = productsData.map((addon) => ({
        user_id: user_id,
        cart_id: cart_id,
        product_id: addon.id,  // Store product_id
        name: addon.name,       // Store name
        unit_price: addon.price,
        type: addon.type,
        product_image: addon.product_image
      }));

      setAddonProducts(updatedAddonProducts);
    };

    fetchAddonProductsData();
  }, [access_token, user_id]);

  useEffect(() => {
    const newSubtotal = cart.reduce(
      (acc, item) => acc + item.quantity * (item.productDetails?.unit_price || item.unit_price || 0),
      0
    );
    setSubtotal(newSubtotal);
  }, [cart]);

  const applyCoupon = async () => {
    try {
      const response = await axios.post(`${baseUrl}/apply_coupon`, {
        coupon_code: couponCode,
      });
      if (response.data.discount) {
        setDiscount(response.data.discount);
      } else {
        alert("Invalid Coupon Code");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      alert("There was an error applying the coupon.");
    }
  };

  const updateQuantity = async (product_id, change) => {
    let cartItem;
    try {
      cartItem = cart.find(item => item.product_id === product_id);
      if (!cartItem) {
        console.error("Cart item not found");
        return;
      }

      const newQuantity = Math.max(1, cartItem.quantity + change);
      if (newQuantity === cartItem.quantity) return;

      // Update the UI optimistically
      setCart((prevCart) => {
        const updatedCart = prevCart.map((item) => {
          if (item.product_id === product_id) {
            return {
              ...item,
              quantity: newQuantity,
            };
          }
          return item;
        });
        
        // Calculate new subtotal with the updated cart data
        const newSubtotal = updatedCart.reduce(
          (acc, item) => acc + item.quantity * (item.productDetails?.unit_price || item.unit_price || 0),
          0
        );
        setSubtotal(newSubtotal);
        
        return updatedCart;
      });

      const updatedCartData = {
        user_id: user_id,
        product_id: product_id,
        cart_id: cartItem.cart_id,
        quantity: newQuantity,
        unit_price: cartItem.unit_price,
        updated_at: new Date().toISOString(),
      };

      await updateCart(cartItem.id, updatedCartData, access_token);

    } catch (error) {
      console.error("Error updating quantity:", error);
      // Revert to original state if there's an error
      setCart((prevCart) => {
        const revertedCart = prevCart.map((item) => {
          if (item.product_id === product_id) {
            return {
              ...item,
              quantity: cartItem.quantity,
            };
          }
          return item;
        });
        
        // Recalculate subtotal with reverted cart data
        const revertedSubtotal = revertedCart.reduce(
          (acc, item) => acc + item.quantity * (item.productDetails?.unit_price || item.unit_price || 0),
          0
        );
        setSubtotal(revertedSubtotal);
        
        return revertedCart;
      });
    }
  };

  const removeItem = async (item) => {
    try {
      await deleteCartItems(item.id, access_token)

      // Refresh cart data
      await fetchCartItems();
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const addAddonToCart = async (addonProduct) => {
    try {
      const isAddonInCart = cart.some(item => item.product_id === addonProduct.product_id);

      if (isAddonInCart) {
        alert("This addon service is already added to your cart.");
        return;
      }

      const cartData = {
        user_id,
        product_id: addonProduct.product_id,
        quantity: 1,
        cart_id: addonProduct.cart_id,
        unit_price: addonProduct.unit_price,
        created_at: new Date().toISOString(),
      }
      const response = await addToCart(cartData, access_token);

      // Refresh cart data
      await fetchCartItems();
    } catch (error) {
      console.error("Error adding addon to cart:", error);
    }
  };

  if (loading) {
    return (
      <div className="spinner">
        {loading ? (
          <div className="d-flex justify-content-center">
            <ClipLoader color="var(--theme-color)" size={50} />
          </div>
        ) : (
          <div>Your content goes here</div>
        )}
      </div>
    )
  }

  const finalTotal = (subtotal - discount).toFixed(2);

  return (
    <div>
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>Cart</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <a href="/"><i className="fa-solid fa-house"></i></a>
                    </li>
                    <li className="breadcrumb-item active">Cart</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cart-section section-b-space">
        <div className="container-fluid-lg">
          <div className="row g-sm-5 g-3">
            <div className="col-xxl-9">
              <div className="cart-table">
                <div className="table-responsive-xl">
                  <table className="table">
                    <tbody>
                      {cart.map((item) => (
                        <tr className="product-box-contain" key={item.id || item.product_id}>
                          <td className="product-detail">
                            <div className="product border-0">
                              <a href={`/product/${item.product_id}`} className="product-image">
                                {item.productDetails && (
                                  <img
                                    src={`../assets/images/products/${item.productDetails.product_image}`}
                                    className="img-fluid blur-up lazyload rounded-corners"
                                    alt={item.productDetails.name || "Product Image"}
                                  />
                                )}
                              </a>
                              <div className="product-detail">
                                <ul>
                                  <li className="name">
                                    <a href={`/product/${item.product_id}`}>
                                      {item.productDetails?.name || "Product Name"}
                                    </a>
                                  </li>
                                  <li className="text-content">
                                    <span className="text-title">Quantity :</span> {item.quantity}
                                  </li>
                                  <li>
                                    <h5 className="text-content d-inline-block">Price : </h5>
                                    <span> &nbsp;&nbsp;₹{item.unit_price}</span>
                                  </li>
                                  <li>
                                    <h5>Total: ₹{(item.quantity * item.unit_price).toFixed(2)}</h5>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </td>
                          <td className="quantity">
                            <h4 className="table-title text-content" style={{ textAlign: "left" }}>Qty</h4>
                            <div className="quantity-price">
                              <div className="cart_qty">
                                <div className="input-group">
                                  <button
                                    type="button"
                                    className="btn qty-left-minus"
                                    onClick={() => updateQuantity(item.product_id, -1)}
                                  >
                                    <i className="fa fa-minus ms-0"></i>
                                  </button>
                                  <input
                                    className="form-control input-number qty-input"
                                    type="text"
                                    name="quantity"
                                    value={item.quantity}
                                    readOnly
                                  />
                                  <button
                                    type="button"
                                    className="btn qty-right-plus"
                                    onClick={() => updateQuantity(item.product_id, 1)}
                                  >
                                    <i className="fa fa-plus ms-0"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="subtotal">
                            <h4 className="table-title text-content" style={{ textAlign: "left" }}>Total</h4>
                            <h5>₹{(item.quantity * item.unit_price).toFixed(2)}</h5>
                          </td>
                          <td className="save-remove">
                            <h4 className="table-title text-content" style={{ textAlign: "left" }}>Action</h4>
                            <a
                              className="remove close_button"
                              href="#"
                              onClick={() => removeItem(item)}
                            >
                              Remove
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="addon-products mt-4">
                <h2 style={{ marginBottom: '10px' }}>Add on Services</h2>
                <div className="row">
                  {addonProducts.map((addon) => (
                    <div className="col-md-4" key={addon.product_id}>
                      <div className="addon-card shadow-sm mb-4" style={{ height: '150px', width: '300px' }}>
                        <div className="card-body d-flex align-items-center">
                          {/* Image on the Left */}
                          <img
                            src={`../assets/images/products/${addon.product_image}`}
                            alt={addon.product_image}
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '5px', marginRight: '10px' }}
                          />
                          
                          {/* Content on the Right */}
                          <div className="d-flex flex-column justify-content-between flex-grow-1">
                            <h3 className="addon-card-title">{addon.name}</h3>
                            <h4 className="addon-card-text" style={{ color: 'var(--theme-color)', fontSize: '1.2rem' }}>
                              ₹{addon.unit_price}
                            </h4>
                          </div>

                          {/* Plus Button - Fixed alignment and consistent size */}
                          <button
                            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                            onClick={() => addAddonToCart(addon)}
                            style={{
                              marginLeft: '10px',
                              width: '35px',
                              height: '35px',
                              padding: '0',
                              minWidth: '35px'
                            }}
                          >
                            <i className="fa fa-plus" style={{ fontSize: '14px' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-xxl-3">
              <div className="summery-box p-sticky">
                <div className="summery-header">
                  <h3>Cart Total</h3>
                </div>
                <div className="summery-contain">
                  <ul>
                    <li>
                      <h4>Subtotal</h4>
                      <h4 className="price">₹{subtotal.toFixed(2)}</h4>
                    </li>
                  </ul>
                </div>
                <div className="summery-total">
                  <ul>
                    <li className="list-total border-top-0">
                      <h4>Total (INR)</h4>
                      <h4 className="price theme-color">₹{finalTotal}</h4>
                    </li>
                  </ul>
                </div>
                <div className="button-group cart-button">
                  <ul>
                    <li>
                      <button
                        onClick={() => {
                          navigate("/checkout");
                          window.location.reload();
                        }}
                        className="btn btn-animation proceed-btn fw-bold"
                      >

                        Proceed To Checkout
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => navigate("/shop")}
                        className="btn btn-light shopping-button text-dark"
                      >
                        <i className="fa-solid fa-arrow-left-long"></i>Return To Shopping
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Cart;
